import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useGym } from '../contexts/GymContext';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { Card, CardContent } from '../components/ui/card';
import { 
  TrendingUp, 
  TrendingDown,
  Wallet,
  PiggyBank,
  Receipt,
  Clock,
  ChevronRight,
  Activity,
  Target,
  ArrowUpRight,
  ArrowDownRight,
  ArrowRightLeft,
  CreditCard,
  Banknote,
  Lock,
  DollarSign,
  Landmark,
  BarChart3,
  GripVertical,
  Settings2,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { formatCurrency } from '../utils/formatters';
import { DrillDownModal } from '../components/DrillDownModal';
import { CapacidadeInvestimentoModal } from '../components/CapacidadeInvestimentoModal';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  Area, AreaChart, ReferenceLine, BarChart, Bar, Legend, ComposedChart, Cell
} from 'recharts';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const PRIMARY_COLOR = '#22C55E';  // Verde vibrante
const SECONDARY_COLOR = '#10B981';
const DANGER_COLOR = '#EF4444';
const WARNING_COLOR = '#F59E0B';
const SUCCESS_COLOR = '#22C55E';

// Mini Sparkline component
const Sparkline = ({ data, color = PRIMARY_COLOR, height = 40 }) => {
  if (!data || data.length === 0) return null;
  
  const chartData = data.map((value, index) => ({ value, index }));
  
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={chartData}>
        <Line 
          type="linear" 
          dataKey="value" 
          stroke={color} 
          strokeWidth={2}
          dot={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

// Gauge circular component
const CircularGauge = ({ value, max = 100, size = 120, strokeWidth = 8, color = PRIMARY_COLOR }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const percent = Math.min(value / max, 1);
  const offset = circumference - percent * circumference;
  
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      {/* Background circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#27272a"
        strokeWidth={strokeWidth}
      />
      {/* Progress circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className="transition-all duration-1000 ease-out"
      />
    </svg>
  );
};

// Score Gauge (grande, central)
const ScoreGauge = ({ score }) => {
  const getScoreColor = (s) => {
    if (s >= 80) return SUCCESS_COLOR;
    if (s >= 60) return '#84CC16';
    if (s >= 40) return WARNING_COLOR;
    return DANGER_COLOR;
  };
  
  return (
    <div className="relative flex items-center justify-center">
      <CircularGauge 
        value={score} 
        max={100} 
        size={160} 
        strokeWidth={12}
        color={getScoreColor(score)}
      />
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl font-bold text-white">{score}</span>
        <span className="text-xs text-zinc-400">de 100</span>
        <span className="text-xs text-zinc-500 mt-1">Score ALVO</span>
      </div>
    </div>
  );
};

// Indicador de saúde individual
const HealthIndicator = ({ nome, status, cor, valor }) => {
  const getStatusColor = (c) => {
    if (c === 'verde') return SUCCESS_COLOR;
    if (c === 'amarelo') return WARNING_COLOR;
    return DANGER_COLOR;
  };
  
  const statusColor = getStatusColor(cor);
  
  return (
    <div className="flex items-center justify-between py-2 px-3 hover:bg-zinc-800/50 rounded-lg cursor-pointer transition-colors group">
      <div className="flex items-center gap-3">
        <div 
          className="w-2.5 h-2.5 rounded-full"
          style={{ backgroundColor: statusColor }}
        />
        <span className="text-sm text-zinc-300">{nome}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm capitalize" style={{ color: statusColor }}>
          {status}
        </span>
        <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400 transition-colors" />
      </div>
    </div>
  );
};

// Card de métrica principal
const MetricCard = ({ title, value, variacao, historico, icon: Icon, gradient = false }) => {
  const isPositive = variacao >= 0;
  
  return (
    <Card className={`border-zinc-800 ${gradient ? 'bg-gradient-to-br from-emerald-900/40 to-zinc-900' : 'bg-zinc-900'}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            {Icon && (
              <div className="p-2 rounded-lg bg-zinc-800/50">
                <Icon className="w-4 h-4 text-emerald-400" />
              </div>
            )}
            <span className="text-xs font-medium text-zinc-400 uppercase tracking-wider">{title}</span>
          </div>
        </div>
        
        <div className="flex items-end justify-between">
          <div>
            <p className={`text-2xl font-bold font-numbers ${typeof value === 'number' && value < 0 ? 'text-red-400' : 'text-white'}`} data-testid="metric-value">
              {typeof value === 'number' ? formatCurrency(value) : value}
            </p>
            {variacao !== undefined && (
              <div className={`flex items-center gap-1 mt-1 text-xs ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                {isPositive ? (
                  <ArrowUpRight className="w-3 h-3" />
                ) : (
                  <ArrowDownRight className="w-3 h-3" />
                )}
                <span>{Math.abs(variacao)}% vs mês anterior</span>
              </div>
            )}
          </div>
          
          {historico && historico.length > 0 && (
            <div className="w-20 h-10">
              <Sparkline data={historico} color={isPositive ? SUCCESS_COLOR : DANGER_COLOR} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Card de Reserva de Caixa (com gauge)
const ReservaCard = ({ dias, meses, percentual }) => {
  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="p-2 rounded-lg bg-zinc-800/50">
                <Clock className="w-4 h-4 text-emerald-400" />
              </div>
              <span className="text-xs font-medium text-zinc-400 uppercase tracking-wider">Reserva de Caixa</span>
            </div>
            <p className="text-3xl font-bold text-white">
              {dias} <span className="text-lg text-zinc-400">dias</span>
            </p>
            <p className="text-sm text-zinc-500 mt-1">{meses} meses de runway</p>
          </div>
          
          <div className="relative">
            <CircularGauge 
              value={percentual} 
              max={100} 
              size={80} 
              strokeWidth={6}
              color={percentual >= 50 ? SUCCESS_COLOR : percentual >= 25 ? WARNING_COLOR : DANGER_COLOR}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Card de Total Acumulativo (com ícone de cadeado indicando que ignora filtro)
const TotalCard = ({ title, value, icon: Icon, color = 'emerald', description, onClick, testId }) => {
  const colorClasses = {
    emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    red: 'text-red-400 bg-red-500/10 border-red-500/20',
    blue: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
    amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  };
  
  return (
    <div
      className={`p-4 sm:p-5 rounded-xl border h-full ${colorClasses[color]} ${onClick ? 'cursor-pointer hover:brightness-125 transition-all' : ''}`}
      onClick={onClick}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="p-1.5 rounded-lg bg-zinc-800/50 shrink-0">
            <Icon className={`w-4 h-4 ${colorClasses[color].split(' ')[0]}`} />
          </div>
          <span className="text-[10px] sm:text-xs text-zinc-400 uppercase tracking-normal sm:tracking-wider font-medium leading-tight break-words min-w-0">{title}</span>
        </div>
        {onClick ? <ChevronRight className="w-3.5 h-3.5 text-zinc-500 shrink-0" /> : <Lock className="w-3 h-3 text-zinc-600 shrink-0" title="Total acumulativo - não muda com filtro" />}
      </div>
      <p className={`text-lg sm:text-2xl font-bold font-numbers leading-tight break-words ${colorClasses[color].split(' ')[0]}`}>
        {formatCurrency(value)}
      </p>
      {description && <p className="text-[11px] sm:text-xs text-zinc-500 mt-2 leading-snug">{description}</p>}
    </div>
  );
};

// Componente de Barra Horizontal para Estrutura do Caixa
const HorizontalBar = ({ label, value, percentual, color = 'emerald', maxWidth = 100, hint }) => {
  const colorClasses = {
    emerald: 'bg-emerald-500',
    red: 'bg-red-500',
    blue: 'bg-blue-500',
    amber: 'bg-amber-500',
    purple: 'bg-purple-500',
  };
  
  const barWidth = Math.min(Math.max(percentual, 0), maxWidth);
  
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <div className="flex flex-col">
          <span className="text-zinc-300">{label}</span>
          {hint && <span className="text-[11px] text-zinc-500">{hint}</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-white font-numbers font-medium">{formatCurrency(value)}</span>
          <span className="text-zinc-500 w-12 text-right">{percentual.toFixed(0)}%</span>
        </div>
      </div>
      <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
        <div 
          className={`h-full ${colorClasses[color]} rounded-full transition-all duration-500`}
          style={{ width: `${barWidth}%` }}
        />
      </div>
    </div>
  );
};

// Componente para seção arrastável
const SortableSection = ({ id, children, isEditMode }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      {isEditMode && (
        <div
          {...attributes}
          {...listeners}
          className="absolute -left-2 top-1/2 -translate-y-1/2 z-10 p-2 bg-zinc-800 rounded-lg cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity border border-zinc-700 hover:border-emerald-500"
        >
          <GripVertical className="w-4 h-4 text-zinc-400" />
        </div>
      )}
      <div className={isEditMode ? 'ring-2 ring-transparent group-hover:ring-emerald-500/30 rounded-xl transition-all' : ''}>
        {children}
      </div>
    </div>
  );
};

// IDs padrão das seções
const DEFAULT_SECTION_ORDER = [
  'cards-principais',
  'faturado-recebido',
  'totais',
  'estrutura-caixa',
  'graficos-evolucao',
  'fluxo-score',
  'kpis'
];

export const SaudeFinanceiraPage = () => {
  const { selectedGym } = useGym();
  const { user } = useAuth();
  const { settings } = useSettings();
  const { chartAnimations, chartStyle } = settings;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [drillDown, setDrillDown] = useState({ open: false, tipo: null, mes: null, ano: null });
  const openDrill = (tipo) => setDrillDown({ open: true, tipo, mes: data?.mes_referencia, ano: data?.ano_referencia });
  
  // Carregar ordem das seções do localStorage
  const [sectionOrder, setSectionOrder] = useState(() => {
    const saved = localStorage.getItem('saude-financeira-layout');
    return saved ? JSON.parse(saved) : DEFAULT_SECTION_ORDER;
  });

  // Sensores para drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handler para quando uma seção é solta
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setSectionOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);
        
        // Salvar no localStorage
        localStorage.setItem('saude-financeira-layout', JSON.stringify(newOrder));
        toast.success('Layout salvo!');
        
        return newOrder;
      });
    }
  };

  // Resetar para layout padrão
  const resetLayout = () => {
    setSectionOrder(DEFAULT_SECTION_ORDER);
    localStorage.removeItem('saude-financeira-layout');
    toast.success('Layout resetado para padrão');
  };

  const loadData = useCallback(async () => {
    if (!selectedGym) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/saude-financeira?gym_id=${selectedGym.id}`);
      setData(response.data);
    } catch (error) {
      toast.error('Erro ao carregar dados de saúde financeira');
    } finally {
      setLoading(false);
    }
  }, [selectedGym]);

  useEffect(() => {
    if (selectedGym) {
      loadData();
    }
  }, [selectedGym, loadData]);

  const [capModalOpen, setCapModalOpen] = useState(false);
  const [kpisModo, setKpisModo] = useState('geral');
  const [capMensal, setCapMensal] = useState(null);

  useEffect(() => {
    if (!selectedGym) return;
    let active = true;
    api.get(`/capacidade-investimento?gym_id=${selectedGym.id}`)
      .then((res) => { if (active) setCapMensal(res.data?.suficiente ? (res.data?.calculo?.capacidade_mensal ?? 0) : 0); })
      .catch(() => {});
    return () => { active = false; };
  }, [selectedGym, capModalOpen]);


  // Obter nome do usuário
  const getUserName = () => {
    if (user?.name) return user.name.split(' ')[0]; // Primeiro nome
    if (user?.email) return user.email.split('@')[0];
    return 'Usuário';
  };

  // Saudação baseada na hora
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bom dia';
    if (hour < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-zinc-400">Nenhum dado disponível</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 overflow-x-hidden" data-testid="saude-financeira-page">
        {/* Header com saudação e filtros */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-2">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              {getGreeting()}, {getUserName()}!
            </h1>
            <p className="text-zinc-400 mt-2 text-base">
              Aqui está o que realmente importa hoje.
            </p>
            {data.usando_mes_fechado && (
              <div
                className="inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30"
                data-testid="saude-base-mes-fechado"
                title="O mês atual ainda não tem lançamentos. Mostrando o último mês fechado até você começar a preencher o mês corrente."
              >
                <Calendar className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-xs text-amber-200">
                  Base: <strong>{data.mes_referencia}/{data.ano_referencia}</strong> (último mês fechado)
                </span>
              </div>
            )}
          </div>
          
          {/* Filtros */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Botão de editar layout */}
            <button
              onClick={() => setIsEditMode(!isEditMode)}
              className={`h-10 px-4 rounded-lg border text-sm flex items-center gap-2 transition-all ${
                isEditMode 
                  ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400' 
                  : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-600'
              }`}
            >
              <Settings2 className="w-4 h-4" />
              {isEditMode ? 'Salvar' : 'Editar Layout'}
            </button>
            
            {isEditMode && (
              <button
                onClick={resetLayout}
                className="h-10 px-4 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-400 text-sm hover:border-zinc-600 transition-all"
              >
                Resetar
              </button>
            )}
          </div>
        </div>

        {/* Seções arrastáveis */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sectionOrder}
            strategy={verticalListSortingStrategy}
          >
            <div className="flex flex-col gap-6">
              {sectionOrder.map((sectionId) => (
                <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode}>
                  {renderSection(sectionId)}
                </SortableSection>
              ))}
            </div>
          </SortableContext>
        </DndContext>

        <DrillDownModal
          open={drillDown.open}
          onOpenChange={(v) => setDrillDown((d) => ({ ...d, open: v }))}
          gymId={selectedGym?.id}
          tipo={drillDown.tipo}
          mes={drillDown.mes}
          ano={drillDown.ano}
        />

        <CapacidadeInvestimentoModal
          open={capModalOpen}
          onOpenChange={setCapModalOpen}
          gymId={selectedGym?.id}
        />
      </div>
    </DashboardLayout>
  );
  
  // Função para renderizar cada seção
  function renderSection(sectionId) {
    switch (sectionId) {
      case 'cards-principais':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Caixa Disponível"
              value={data.caixa_disponivel?.valor || 0}
              variacao={data.caixa_disponivel?.variacao}
              historico={data.caixa_disponivel?.historico}
              icon={Wallet}
              gradient
            />
            <MetricCard
              title="Resultado do Mês"
              value={data.resultado_mes?.valor || 0}
              variacao={data.resultado_mes?.variacao}
              historico={data.resultado_mes?.historico}
              icon={TrendingUp}
              gradient
            />
            <MetricCard
              title="Receita do Mês"
              value={data.receita_mes?.valor || 0}
              variacao={data.receita_mes?.variacao}
              historico={data.receita_mes?.historico}
              icon={Receipt}
              gradient
            />
            <ReservaCard
              dias={data.reserva_caixa?.dias || 0}
              meses={data.reserva_caixa?.meses || 0}
              percentual={data.reserva_caixa?.percentual || 0}
            />
          </div>
        );
      
      case 'faturado-recebido':
        return (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="p-2 rounded-lg bg-zinc-800/50">
                  <ArrowRightLeft className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-base font-medium text-white">Faturado vs Recebido</h3>
                  <p className="text-xs text-zinc-500">Acumulado — visão geral (todos os meses)</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-5 rounded-xl bg-zinc-800/50 border border-zinc-700 h-full">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-1.5 rounded-lg bg-zinc-700/50">
                      <Receipt className="w-4 h-4 text-emerald-400" />
                    </div>
                    <span className="text-xs text-zinc-400 uppercase tracking-wider font-medium">Faturado</span>
                  </div>
                  <p className="text-2xl font-bold text-emerald-400 font-numbers">
                    <button onClick={() => openDrill('faturamento')} className="hover:underline decoration-dotted underline-offset-2 cursor-pointer" title="Ver composição" data-testid="saude-drill-faturado">
                      {formatCurrency(data.faturado_vs_recebido?.faturado_total || 0)}
                    </button>
                  </p>
                  <p className="text-xs text-zinc-500 mt-2">Total faturado (geral)</p>
                </div>
                
                <div className="p-5 rounded-xl bg-zinc-800/50 border border-zinc-700 h-full">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-1.5 rounded-lg bg-zinc-700/50">
                      <Banknote className="w-4 h-4 text-blue-400" />
                    </div>
                    <span className="text-xs text-zinc-400 uppercase tracking-wider font-medium">Recebido</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-400 font-numbers">
                    <button onClick={() => openDrill('recebido_real')} className="hover:underline decoration-dotted underline-offset-2 cursor-pointer" title="Ver lançamentos do banco (Entrada do Fluxo)" data-testid="saude-drill-recebido">
                      {formatCurrency(data.faturado_vs_recebido?.recebido_total || 0)}
                    </button>
                  </p>
                  <p className="text-xs text-zinc-500 mt-2">Total recebido no banco (geral)</p>
                </div>
                
                <div className="p-5 rounded-xl border h-full bg-amber-500/10 border-amber-500/30">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-1.5 rounded-lg bg-zinc-800/50">
                      <CreditCard className="w-4 h-4 text-amber-400" />
                    </div>
                    <span className="text-xs text-zinc-400 uppercase tracking-wider font-medium">Carteira Total</span>
                  </div>
                  <p className="text-2xl font-bold font-numbers text-amber-400">
                    <button onClick={() => openDrill('carteira')} className="hover:underline decoration-dotted underline-offset-2 cursor-pointer" title="Carteira Total = tudo que você faturou − tudo que já recebeu de cliente (OFX). Divide-se em Liquidada (o que já está em conta) + A Receber (o que ainda falta entrar)." data-testid="saude-drill-gap">
                      {formatCurrency(data.faturado_vs_recebido?.a_receber || 0)}
                    </button>
                  </p>
                  <p className="text-xs text-zinc-500 mt-2">Faturado − Recebido de cliente</p>
                </div>
              </div>
              
              <div className="mt-4 p-4 rounded-xl bg-zinc-800/40 border border-zinc-700/60" data-testid="carteira-decomposicao">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
                  <div className="flex items-center justify-between sm:block sm:flex-1 sm:text-center">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500">Carteira Total</p>
                    <button onClick={() => openDrill('carteira')} className="text-lg font-bold font-numbers text-white hover:underline decoration-dotted underline-offset-2 cursor-pointer" data-testid="carteira-total-valor" title="Ver composição (Faturado − Recebido)">{formatCurrency(data.totais?.carteira_total || 0)}</button>
                  </div>
                  <span className="hidden sm:block text-zinc-600 text-base">=</span>
                  <div className="flex items-center justify-between sm:block sm:flex-1 sm:text-center">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500">Liquidada (em conta)</p>
                    <button onClick={() => openDrill('liquidada')} className="text-lg font-bold font-numbers text-blue-400 hover:underline decoration-dotted underline-offset-2 cursor-pointer" data-testid="carteira-liquidada-valor" title="Ver saldos das contas">{formatCurrency(data.totais?.carteira_liquidada || 0)}</button>
                  </div>
                  <span className="hidden sm:block text-zinc-600 text-base">+</span>
                  <div className="flex items-center justify-between sm:block sm:flex-1 sm:text-center">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500">A Receber</p>
                    <button onClick={() => openDrill('carteira')} className="text-lg font-bold font-numbers text-amber-400 hover:underline decoration-dotted underline-offset-2 cursor-pointer" data-testid="carteira-areceber-valor" title="A Receber = Carteira Total − Liquidada">{formatCurrency(data.totais?.carteira_a_receber || 0)}</button>
                  </div>
                </div>
              </div>

              <div className="mt-4 p-3 rounded-lg bg-zinc-800/30 border border-zinc-700/50">
                <p className="text-xs text-zinc-400">
                  <span className="text-white font-medium">Carteira Total</span> (Faturado − Recebido de cliente) se divide em <span className="text-blue-400 font-medium">Liquidada</span> (o que já está em conta) + <span className="text-amber-400 font-medium">A Receber</span> (o que ainda falta entrar = Total − Liquidada).
                </p>
              </div>
            </CardContent>
          </Card>
        );
      
      case 'totais':
        return (
          <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <TotalCard
              title="Carteira Total"
              value={data.totais?.carteira_total || 0}
              icon={CreditCard}
              color="emerald"
              description="Faturado − Recebido de cliente"
            />
            <TotalCard
              title="Compromissos"
              value={data.totais?.compromissos_total || 0}
              icon={Landmark}
              color="red"
              description="Saldo devedor"
            />
            <TotalCard
              title="Caixa Livre"
              value={data.totais?.caixa_livre || 0}
              icon={Wallet}
              color={(data.totais?.caixa_livre || 0) >= 0 ? 'emerald' : 'red'}
              description="Caixa - Compromissos"
            />
            <TotalCard
              title="Cap. Investir"
              value={capMensal != null ? capMensal : (data.totais?.cap_investir_mensal || 0)}
              icon={DollarSign}
              color="blue"
              description="Quanto pode comprometer/mês — clique para ver o cálculo"
              onClick={() => setCapModalOpen(true)}
              testId="cap-investir-card"
            />
          </div>
          <div className="mt-3 flex items-center gap-2 p-3 rounded-lg bg-zinc-800/30 border border-zinc-700/50" data-testid="cap-investir-config">
            <Target className="w-4 h-4 text-blue-400 shrink-0" />
            <span className="text-xs text-zinc-400 flex-1">
              A capacidade de investimento é calculada a partir do seu fluxo (geração, compromissos, reserva e teste de estresse).
            </span>
            <button
              onClick={() => setCapModalOpen(true)}
              className="text-xs px-3 py-1.5 rounded bg-blue-500/20 border border-blue-500/40 text-blue-300 hover:bg-blue-500/30 shrink-0"
              data-testid="cap-investir-ver-calculo"
            >
              Ver cálculo e ajustar
            </button>
          </div>
        </div>
        );
      
      case 'estrutura-caixa':
        return (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="p-2 rounded-lg bg-zinc-800/50">
                  <BarChart3 className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-base font-medium text-white">Estrutura do Caixa</h3>
                  <p className="text-xs text-zinc-500">Como o dinheiro recebido foi usado (caixa real) — {data.mes_referencia} {data.ano_referencia}</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <HorizontalBar
                  label="Recebido"
                  hint={`Taxa de recebimento — de ${formatCurrency(data.estrutura_caixa?.recebido?.faturado || 0)} faturado`}
                  value={data.estrutura_caixa?.recebido?.valor || 0}
                  percentual={data.estrutura_caixa?.recebido?.percentual || 0}
                  color="blue"
                />
                <HorizontalBar
                  label="Saídas Operacionais"
                  value={data.estrutura_caixa?.gastos_operacionais?.valor || 0}
                  percentual={data.estrutura_caixa?.gastos_operacionais?.percentual || 0}
                  color="red"
                />
                <HorizontalBar
                  label="Despesa Pontual"
                  value={data.estrutura_caixa?.despesa_pontual?.valor || 0}
                  percentual={data.estrutura_caixa?.despesa_pontual?.percentual || 0}
                  color="amber"
                />
                <HorizontalBar
                  label="Investimento"
                  value={data.estrutura_caixa?.investimento?.valor || 0}
                  percentual={data.estrutura_caixa?.investimento?.percentual || 0}
                  color="purple"
                />
                <HorizontalBar
                  label="Sobra de Caixa"
                  value={data.estrutura_caixa?.sobra_caixa?.valor || 0}
                  percentual={Math.max(0, data.estrutura_caixa?.sobra_caixa?.percentual || 0)}
                  color="emerald"
                />
              </div>
            </CardContent>
          </Card>
        );
      
      case 'graficos-evolucao':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Gráfico Faturado vs Recebido */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <ArrowRightLeft className="w-5 h-5 text-zinc-400" />
                    <h3 className="text-base font-medium text-white">Faturado vs Recebido</h3>
                  </div>
                  <div className="flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-emerald-500 rounded"></div>
                      <span className="text-zinc-400">Faturado</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-blue-500 rounded"></div>
                      <span className="text-zinc-400">Recebido</span>
                    </div>
                  </div>
                </div>
                
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={data.graficos?.faturado_vs_recebido || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                      <XAxis dataKey="mes" axisLine={false} tickLine={false} tick={{ fill: '#71717a', fontSize: 10 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: '#71717a', fontSize: 10 }} 
                        tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }}
                        formatter={(value, name) => [formatCurrency(value), name === 'faturado' ? 'Faturado' : 'Recebido']}
                      />
                      <Bar dataKey="faturado" fill="#22C55E" radius={[4, 4, 0, 0]} isAnimationActive={chartAnimations} />
                      <Bar dataKey="recebido" fill="#3B82F6" radius={[4, 4, 0, 0]} isAnimationActive={chartAnimations} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Gráfico Evolução do Caixa */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Wallet className="w-5 h-5 text-zinc-400" />
                  <div>
                    <h3 className="text-base font-medium text-white">Evolução do Caixa</h3>
                    <p className="text-xs text-zinc-500">Saldo real em conta, mês a mês (mesma base do Fluxo) — até o último mês fechado</p>
                  </div>
                </div>
                
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.graficos?.evolucao_caixa || []}>
                      <defs>
                        <linearGradient id="colorCaixa" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22C55E" stopOpacity={chartStyle === 'minimal' ? 0.1 : 0.3}/>
                          <stop offset="95%" stopColor="#22C55E" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                      <XAxis dataKey="mes" axisLine={false} tickLine={false} tick={{ fill: '#71717a', fontSize: 10 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: '#71717a', fontSize: 10 }}
                        tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }}
                        formatter={(value) => [formatCurrency(value), 'Saldo']}
                      />
                      <Area 
                        type="linear" 
                        dataKey="saldo" 
                        stroke="#22C55E" 
                        strokeWidth={chartStyle === 'minimal' ? 1 : 2} 
                        fill={chartStyle === 'minimal' ? 'none' : 'url(#colorCaixa)'}
                        isAnimationActive={chartAnimations}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Gráfico A Receber — Gap do mês (Gestão de Receita, não acumulado) */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <CreditCard className="w-5 h-5 text-zinc-400" />
                  <div>
                    <h3 className="text-base font-medium text-white">A Receber — Gap do Mês</h3>
                    <p className="text-xs text-zinc-500">Quanto do faturado de cada mês é a prazo e ainda vai cair (pela Gestão de Receita). É o mesmo "A Receber" do Fluxo. Não é acumulado.</p>
                  </div>
                </div>
                
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.graficos?.carteira_evolucao || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                      <XAxis dataKey="mes" axisLine={false} tickLine={false} tick={{ fill: '#71717a', fontSize: 10 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: '#71717a', fontSize: 10 }}
                        tickFormatter={(v) => Math.abs(v) >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }}
                        formatter={(value) => [formatCurrency(value), 'A Receber (gap do mês)']}
                        cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                      />
                      <ReferenceLine y={0} stroke="#3f3f46" />
                      <Bar dataKey="gap" radius={[4, 4, 0, 0]} isAnimationActive={chartAnimations}>
                        {(data.graficos?.carteira_evolucao || []).map((entry, idx) => (
                          <Cell key={idx} fill={entry.gap >= 0 ? '#F59E0B' : '#22C55E'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Gráfico Saldo Devedor */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Landmark className="w-5 h-5 text-zinc-400" />
                    <h3 className="text-base font-medium text-white">Saldo Devedor</h3>
                  </div>
                  {data.graficos?.ultima_parcela && (
                    <span className="text-xs text-zinc-500">
                      Até {data.graficos.ultima_parcela.mes}/{data.graficos.ultima_parcela.ano}
                    </span>
                  )}
                </div>
                
                <div className="h-52">
                  {data.graficos?.saldo_devedor?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={data.graficos.saldo_devedor}>
                        <defs>
                          <linearGradient id="colorDevedor" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#EF4444" stopOpacity={chartStyle === 'minimal' ? 0.1 : 0.3}/>
                            <stop offset="95%" stopColor="#EF4444" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                        <XAxis dataKey="mes" axisLine={false} tickLine={false} tick={{ fill: '#71717a', fontSize: 9 }} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#71717a', fontSize: 10 }}
                          tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }}
                          formatter={(value) => [formatCurrency(value), 'Saldo Devedor']}
                        />
                        <Area 
                          type="linear" 
                          dataKey="saldo" 
                          stroke="#EF4444" 
                          strokeWidth={chartStyle === 'minimal' ? 1 : 2} 
                          fill={chartStyle === 'minimal' ? 'none' : 'url(#colorDevedor)'}
                          isAnimationActive={chartAnimations}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-zinc-500 text-sm">
                      Nenhum compromisso cadastrado
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        );
      
      case 'fluxo-score':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Gráfico de Projeção de Fluxo de Caixa */}
            <Card className="bg-zinc-900 border-zinc-800 lg:col-span-2">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-emerald-400" />
                    <h3 className="text-base font-medium text-white">Fluxo de Caixa Projetado</h3>
                  </div>
                  <span className="text-xs text-zinc-500">Próximos 12 meses</span>
                </div>
                
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.projecao_fluxo || []}>
                      <defs>
                        <linearGradient id="colorProjecao" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22C55E" stopOpacity={chartStyle === 'minimal' ? 0.1 : 0.3}/>
                          <stop offset="95%" stopColor="#22C55E" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                      <XAxis dataKey="mes" axisLine={false} tickLine={false} tick={{ fill: '#71717a', fontSize: 10 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: '#71717a', fontSize: 10 }}
                        tickFormatter={(value) => value >= 1000 ? `${(value/1000).toFixed(0)}k` : value} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }}
                        formatter={(value) => [formatCurrency(value), 'Projetado']}
                      />
                      <ReferenceLine y={0} stroke="#EF4444" strokeDasharray="3 3" />
                      <Area 
                        type="linear" 
                        dataKey="saldo" 
                        stroke="#22C55E" 
                        strokeWidth={chartStyle === 'minimal' ? 1 : 2} 
                        fill={chartStyle === 'minimal' ? 'none' : 'url(#colorProjecao)'}
                        isAnimationActive={chartAnimations}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Score de Saúde Financeira */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Target className="w-5 h-5 text-emerald-400" />
                  <h3 className="text-base font-medium text-white">Saúde Financeira</h3>
                </div>
                
                <div className="flex flex-col items-center">
                  {/* Score Circular */}
                  <div className="relative w-40 h-40">
                    <svg className="w-full h-full transform -rotate-90">
                      {/* Background circle */}
                      <circle
                        cx="80" cy="80" r="70"
                        fill="none"
                        stroke="#27272a"
                        strokeWidth="12"
                      />
                      {/* Progress circle */}
                      <circle
                        cx="80" cy="80" r="70"
                        fill="none"
                        stroke={data.score?.valor >= 70 ? '#22C55E' : data.score?.valor >= 40 ? '#F59E0B' : '#EF4444'}
                        strokeWidth="12"
                        strokeLinecap="round"
                        strokeDasharray={`${(data.score?.valor || 0) * 4.4} 440`}
                        className="transition-all duration-1000"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-4xl font-bold text-white">{data.score?.valor || 0}</span>
                      <span className="text-xs text-zinc-500">de 100</span>
                    </div>
                  </div>
                  
                  <div className="mt-4 text-center">
                    <span className={`text-sm font-medium ${
                      data.score?.valor >= 70 ? 'text-emerald-400' : 
                      data.score?.valor >= 40 ? 'text-amber-400' : 'text-red-400'
                    }`}>
                      {data.score?.valor >= 70 ? 'Saudável' : data.score?.valor >= 40 ? 'Atenção' : 'Crítico'}
                    </span>
                    <p className="text-xs text-zinc-500 mt-1">Score ALVO</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );
      
      case 'kpis': {
        const kpiModo = data.kpis_modos?.[kpisModo] || null;
        return (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <ChevronRight className="w-5 h-5 text-emerald-400 shrink-0" />
                  <div className="min-w-0">
                    <h3 className="text-base font-medium text-white">Indicadores Chave</h3>
                    <p className="text-xs text-zinc-500">
                      {kpisModo === 'geral'
                        ? 'Geral — todas as entradas e saídas (inclui aportes, pontuais, investimentos, retiradas)'
                        : 'Operacional — só receitas e custos da operação (DRE)'}
                    </p>
                  </div>
                </div>
                <div className="flex rounded-lg border border-zinc-700 overflow-hidden shrink-0" data-testid="kpis-modo-toggle">
                  {['geral', 'operacional'].map((m) => (
                    <button
                      key={m}
                      onClick={() => setKpisModo(m)}
                      data-testid={`kpis-modo-${m}`}
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                        kpisModo === m ? 'bg-emerald-500/20 text-emerald-300' : 'bg-transparent text-zinc-400 hover:text-white'
                      }`}
                    >
                      {m === 'geral' ? 'Geral' : 'Operacional'}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {[
                  { label: 'Margem Líquida', value: kpiModo?.margem_liquida, suffix: '%', good: (v) => v >= 10 },
                  { label: 'ROI', value: kpiModo?.roi, suffix: '%', good: (v) => v >= 15 },
                  { label: 'Liquidez', value: kpiModo?.liquidez, suffix: 'x', good: (v) => v >= 1.5 },
                  { label: 'Endividamento', value: kpiModo?.endividamento, suffix: '%', good: (v) => v <= 30 },
                  { label: 'Burn Rate', value: kpiModo?.burn_rate, format: true, good: () => true },
                ].map((kpi) => (
                  <div key={kpi.label} className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
                    <p className="text-xs text-zinc-500 mb-1">{kpi.label}</p>
                    <p className={`text-lg font-bold font-numbers ${
                      kpi.good(kpi.value) ? 'text-emerald-400' : 'text-amber-400'
                    }`}>
                      {kpi.prefix || ''}{kpi.format ? formatCurrency(kpi.value || 0) : (kpi.value?.toFixed(1) || '0')}{!kpi.format && (kpi.suffix || '')}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      }
      
      default:
        return null;
    }
  }
};

export default SaudeFinanceiraPage;
